import { useEffect, useState } from 'react';
import {
  api,
  getApiErrorMessage,
  triggerFileDownload,
  type SupplierRosterExportParams,
  type SupplierRosterPreviewResult,
} from '../api';
import { Button } from '../ui';

interface SupplierRosterModalProps {
  isOpen: boolean;
  onClose: () => void;
  params: SupplierRosterExportParams;
  scopeTitle?: string;
  onNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
}

export function SupplierRosterModal({
  isOpen,
  onClose,
  params,
  scopeTitle = '供應商基本資料及藥品清冊匯出',
  onNotify,
}: SupplierRosterModalProps) {
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewData, setPreviewData] = useState<SupplierRosterPreviewResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showAllMissing, setShowAllMissing] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setPreviewData(null);
      setError(null);
      setShowAllMissing(false);
      return;
    }

    let isCancelled = false;
    setLoadingPreview(true);
    setError(null);

    api.supplierRoster
      .preview(params)
      .then((data) => {
        if (!isCancelled) {
          setPreviewData(data);
          setLoadingPreview(false);
        }
      })
      .catch((err) => {
        if (!isCancelled) {
          setError(getApiErrorMessage(err, '無法載入清冊預覽資料'));
          setLoadingPreview(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [isOpen, params.vendorId, JSON.stringify(params.productIds), params.categoryCode]);

  if (!isOpen) return null;

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const { blob, fileName } = await api.supplierRoster.export(params);
      triggerFileDownload(blob, fileName);
      onNotify?.(`已成功下載匯出檔：${fileName}`, 'success');
      onClose();
    } catch (err) {
      const msg = getApiErrorMessage(err, '匯出清冊失敗，請稍後再試。');
      setError(msg);
      onNotify?.(msg, 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const isMultiVendor = (previewData?.totalVendors ?? 0) > 1;
  const missingItems = previewData?.allMissingItems ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in overflow-y-auto">
      <div
        className="relative w-full max-w-3xl my-8 overflow-hidden bg-white border border-slate-200 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]"
        role="dialog"
        aria-modal="true"
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/80">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-teal-100 text-teal-700 font-bold text-sm">
                📊
              </span>
              <h2 className="text-lg font-bold text-slate-900">匯出供應商基本資料及藥品清冊</h2>
            </div>
            <p className="mt-1 text-xs text-slate-500 flex items-center gap-2">
              <span>範圍：{scopeTitle}</span>
              <span className="text-slate-300">|</span>
              <span className="font-mono text-teal-700 font-medium">
                正式範本 v1.0 (SHA256: dbbd9fc1...)
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200/60 transition"
            aria-label="關閉"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {loadingPreview && (
            <div className="py-16 text-center space-y-3">
              <div className="w-10 h-10 mx-auto border-3 border-teal-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm font-medium text-slate-600">
                正在分析匯出資料與檢查欄位品質...
              </p>
            </div>
          )}

          {error && (
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-sm flex items-start gap-3">
              <span className="text-rose-500 font-bold text-base mt-0.5">⚠️</span>
              <div className="flex-1">
                <p className="font-bold">分析或匯出過程發生錯誤</p>
                <p className="mt-0.5 text-rose-700 text-xs">{error}</p>
              </div>
            </div>
          )}

          {!loadingPreview && previewData && (
            <>
              {/* Summary Metric Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                  <span className="text-xs text-slate-500 font-medium block">目標廠商數</span>
                  <span className="text-2xl font-black text-slate-800 mt-1 block">
                    {previewData.totalVendors}{' '}
                    <span className="text-xs font-normal text-slate-500">家</span>
                  </span>
                </div>
                <div className="p-3.5 bg-teal-50/60 rounded-xl border border-teal-100">
                  <span className="text-xs text-teal-700 font-medium block">Sheet 2 藥品清冊</span>
                  <span className="text-2xl font-black text-teal-900 mt-1 block">
                    {previewData.vendors.reduce((s, v) => s + v.medicineCount, 0)}{' '}
                    <span className="text-xs font-normal text-teal-700">品項</span>
                  </span>
                </div>
                <div className="p-3.5 bg-blue-50/60 rounded-xl border border-blue-100">
                  <span className="text-xs text-blue-700 font-medium block">
                    Sheet 3 非藥商清冊
                  </span>
                  <span className="text-2xl font-black text-blue-900 mt-1 block">
                    {previewData.vendors.reduce((s, v) => s + v.nonMedicineCount, 0)}{' '}
                    <span className="text-xs font-normal text-blue-700">品項</span>
                  </span>
                </div>
                <div
                  className={`p-3.5 rounded-xl border ${
                    missingItems.length > 0
                      ? 'bg-amber-50/60 border-amber-200'
                      : 'bg-emerald-50/60 border-emerald-100'
                  }`}
                >
                  <span
                    className={`text-xs font-medium block ${
                      missingItems.length > 0 ? 'text-amber-800' : 'text-emerald-800'
                    }`}
                  >
                    資料缺漏提醒
                  </span>
                  <span
                    className={`text-2xl font-black mt-1 block ${
                      missingItems.length > 0 ? 'text-amber-900' : 'text-emerald-900'
                    }`}
                  >
                    {missingItems.length} <span className="text-xs font-normal">項</span>
                  </span>
                </div>
              </div>

              {/* Vendors List & Status */}
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 text-xs font-bold text-slate-700 flex justify-between items-center">
                  <span>廠商與工作表填寫狀態</span>
                  {isMultiVendor && (
                    <span className="text-teal-700 font-medium">
                      批次模式（將打包為 .zip 下載）
                    </span>
                  )}
                </div>
                <div className="divide-y divide-slate-100 max-h-56 overflow-y-auto">
                  {previewData.vendors.map((v) => {
                    const hasQuestionnaire = !!v.assessmentFilledDate;
                    return (
                      <div key={v.vendorId} className="p-3.5 hover:bg-slate-50/50 transition">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs font-bold bg-slate-200/80 px-2 py-0.5 rounded text-slate-700">
                              {v.vendorCode}
                            </span>
                            <span className="text-sm font-bold text-slate-900">{v.vendorName}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200 font-medium">
                              藥品 {v.medicineCount} 筆
                            </span>
                            <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                              非藥商 {v.nonMedicineCount} 筆
                            </span>
                          </div>
                        </div>

                        {/* Questionnaire Status */}
                        <div className="mt-2 flex items-center justify-between text-xs text-slate-600">
                          <div className="flex items-center gap-1.5">
                            <span className="text-slate-400">Sheet 1 問卷評估：</span>
                            {hasQuestionnaire ? (
                              <span className="text-emerald-700 font-medium flex items-center gap-1">
                                <svg
                                  className="w-3.5 h-3.5"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                已完成 44 題（填寫日期：{v.assessmentFilledDate}）
                              </span>
                            ) : (
                              <span className="text-amber-700 font-medium flex items-center gap-1">
                                <svg
                                  className="w-3.5 h-3.5"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                未完成 44 題問卷（將產出空白題目）
                              </span>
                            )}
                          </div>
                          {v.missingItems.length > 0 && (
                            <span className="text-amber-600 font-medium">
                              {v.missingItems.length} 個欄位待留意
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Quality & Missing Fields Section */}
              {missingItems.length > 0 ? (
                <div className="p-4 rounded-xl bg-amber-50/70 border border-amber-200/80 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-amber-900 font-bold text-sm">
                      <span>⚠️</span>
                      <span>匯出資料品質檢核提醒（共 {missingItems.length} 項）</span>
                    </div>
                    {missingItems.length > 3 && (
                      <button
                        type="button"
                        onClick={() => setShowAllMissing((prev) => !prev)}
                        className="text-xs text-amber-800 hover:text-amber-900 underline font-medium"
                      >
                        {showAllMissing ? '收合清單' : `查看全部 ${missingItems.length} 項`}
                      </button>
                    )}
                  </div>

                  <p className="text-xs text-amber-700 leading-relaxed">
                    以下項目資料尚有缺漏，系統將如實保留空格並維持原始 Excel
                    格式匯出。建議後續於主檔中補齊：
                  </p>

                  <div className="space-y-1.5 pt-1">
                    {(showAllMissing ? missingItems : missingItems.slice(0, 3)).map((item, idx) => (
                      <div
                        key={idx}
                        className="text-xs bg-white/80 p-2 rounded-lg border border-amber-200/60 flex items-start justify-between gap-2"
                      >
                        <div>
                          <span className="font-bold text-slate-800">
                            {item.product_name
                              ? `[${item.product_name}] `
                              : `[${item.vendor_name}] `}
                          </span>
                          <span className="text-slate-600">{item.description}</span>
                        </div>
                        <span
                          className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                            item.severity === 'CRITICAL'
                              ? 'bg-rose-100 text-rose-700'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {item.severity === 'CRITICAL' ? '重要' : '提醒'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-emerald-600 shrink-0"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>資料品質檢核完全通過：統編、條碼、藥品許可證與成分資料齊全！</span>
                </div>
              )}

              {/* Template Guarantee Notice */}
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs text-slate-500 space-y-1">
                <div className="font-semibold text-slate-700">匯出格式保證：</div>
                <ul className="list-disc list-inside space-y-0.5 text-slate-600">
                  <li>嚴格維持 3 張工作表原始順序（廠商基本資料、藥品清冊、非藥商清冊）</li>
                  <li>保留欄位寬度、合併儲存格、框線、B3 批註及第 73、74 列留空簽章區</li>
                  <li>超過 32 筆之商品將自動延展列並同步更新列印範圍及標題重複列</li>
                </ul>
              </div>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <Button variant="ghost" onClick={onClose} disabled={isExporting}>
            取消
          </Button>

          <Button
            variant="primary"
            loading={isExporting}
            disabled={loadingPreview || !!error || !previewData || previewData.totalProducts === 0}
            onClick={handleExport}
            className="px-6"
          >
            {isExporting ? (
              '產製檔案中...'
            ) : isMultiVendor ? (
              <>
                <span>下載全部 ({previewData?.totalVendors} 家 .zip)</span>
              </>
            ) : (
              <>
                <span>確認下載清冊 (.xlsx)</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
